import React from 'react';
import { digitalCardDescription } from '../utils/digitalCardDescription';
import { corruptionPresentation } from '../utils/corruptionPresentation';
import { Ally, CommonItem, Corruption, Gate, Injury, Investigator, Madness, PersonalStory, Skill, SpecialCard, Spell, UniqueItem } from '../api';
import { InvestigatorDisplay } from './InvestigatorDisplay';
import { SimpleMarkdown } from './SimpleMarkdown';
import { GateMarker } from './GateMarker';
import { PersonalStoryCard } from './PersonalStory';
import { RelationshipCard } from './Relationship';
import type { Relationship } from '../api';
import { useMonsterTokenStyle } from '../utils/clientSettings';
import { DigitalItemCard } from './DigitalItemCard';
import { getDigitalItemArtworkPath } from '../utils/itemArtwork';

export type ItemKind = 'common' | 'unique' | 'spell' | 'skill' | 'ally' | 'special' | 'ancientOne' | 'madness' | 'injury' | 'corruption' | 'investigator' | 'gate' | 'personalStory' | 'relationship';
export type StackedItem =
  | CommonItem
  | UniqueItem
  | Spell
  | Skill
  | Ally
  | SpecialCard
  | Injury
  | Madness
  | Corruption
  | PersonalStory
  | Relationship;

export interface StackedItemWithKind {
  item: StackedItem;
  kind: ItemKind;
}

export type CardProps = {
  card: StackedItemWithKind
}

export const normalizeCardUri = (name: string) => name.replace(/\s+/g, '').replaceAll('.', '').replaceAll("'", '').replaceAll('"', '');

const uniqueItemAssetAliases: Record<string, string> = {
  CarcosanPage: 'CarcoseanPage',
};

export const getItemImagePath = (kind: ItemKind, name: string, isNodensBlessing = false) => {
  if (!name || !name.replace) {
    return '';
  }

  if (isNodensBlessing) {
    return '/Special/NodensBlessing.jpg';
  }

  const normalized = normalizeCardUri(name);

  switch (kind.toLocaleLowerCase()) {
    case 'common':
      return `/CommonItems/${encodeURIComponent(normalized)}.jpg`;
    case 'unique':
      return `/UniqueItems/${encodeURIComponent(uniqueItemAssetAliases[normalized] ?? normalized)}.jpg`;
    case 'spell':
      return `/Spells/${encodeURIComponent(normalized)}.jpg`;
    case 'ally':
      return `/Allies/${encodeURIComponent(normalized)}.jpg`;
    case 'skill':
      return '/Skills/Skill.jpg';
    case 'special':
      if (normalized === 'MiskatonicStudent' || normalized === 'MiskatonicAlumnus') {
        return `/Special/${normalized}.png`;
      }
      return `/Special/${encodeURIComponent(normalized)}.jpg`;
    case 'ancientOne':
      return `/AncientOnes/${encodeURIComponent(normalized)}.png`;
    case 'madness':
      return '/Madness.jpg';
    case 'injury':
      return '/Injury.jpg';
    case 'personalstory':
      return '/Skills/Skill.jpg';
    default:
      return '';
  }
};

export const getTitleCover = (kind: ItemKind) => {
    switch (kind) {
      case 'spell':
        return '/Spells/titleCover.jpg';
      case 'unique':
        return '/UniqueItems/titleCover.jpg';
      case 'ally':
        return '/Allies/titleCover.jpg';
      case 'common':
        return '/CommonItems/titleCover.jpg';
      default:
        return '';
  }
}

const isMagicalCombatSpell =
  (name: string) =>
    name === "Wither" ||
    name === "Shrivelling" ||
    name === "Dread Curse of Azathoth" ||
    name === "Storm of Spirits" ||
    name === "Spectral Razor" ||
    name === "Steal Life" ||
    name === "Fist of Yog-Sothoth" ||
    name === "Azure Flame";

export const getDescription = (card: StackedItemWithKind): string => {
  if (card.kind === 'corruption') return (card.item as Corruption).textDescription?.trim() || '';
  const description = card.item.description?.trim() || '';
  const spellCard = card.item as Spell;
  const isTome =
    (card.kind === 'common' || card.kind === 'unique') &&
    (card.item as CommonItem | UniqueItem).type === 'Tome';
  const isPhysicalWeapon =
    (card.kind === 'common' || card.kind === 'unique' || card.kind === 'special') &&
    (card.item as CommonItem | UniqueItem | SpecialCard).type === 'PhysicalWeapon';
  const isMagicalWeapon =
    ((card.kind === 'common' || card.kind === 'unique' || card.kind === 'special') &&
    (card.item as CommonItem | UniqueItem | SpecialCard).type === 'MagicalWeapon');
  const isMagicalSpell = (card.kind === 'spell' && isMagicalCombatSpell((card.item as Spell).name));
  const isTask =
    (card.kind === 'common') &&
    (card.item as CommonItem).type === 'Task';
  const isMission =
    (card.kind === 'unique') &&
    (card.item as UniqueItem).type === 'Mission';
  const isSpell = (card.kind === 'spell');
  const spellDetails: string[] = [];

  if (isSpell && spellCard.sanityCost !== undefined) {
    spellDetails.push(`*Sanity cost*: **${spellCard.sanityCost}**`);
  }
  if (isSpell && spellCard.castingModifier !== undefined) {
    const modifier =
      spellCard.castingModifier === 99 && spellCard.name === 'Call Ancient One'
        ? '−X'
        : spellCard.castingModifier === 99 ? 'X' : spellCard.castingModifier;
    spellDetails.push(`*Modifier*: **${modifier}**`);
  }
  const spellPrefix = spellDetails.length > 0 ? `${spellDetails.join(' ')}. ` : '';

  return `${spellPrefix}${isPhysicalWeapon ? '**Physical Weapon**\n' : ''}${isMagicalWeapon ? '**Magical Weapon**\n' : ''}${isTome ? '**Tome**\n' : ''}${isTask ? '**Task**\n' : ''}${isMission ? '**Mission**\n' : ''}${isMagicalSpell ? 'Magical spell\n' : ''}${description}`;
}

const isTaskCommonItemCard = (card: StackedItemWithKind): boolean =>
  card.kind === 'common' && (card.item as CommonItem).type === 'Task';

const isMissionUniqueItemCard = (card: StackedItemWithKind): boolean =>
  card.kind === 'unique' && (card.item as UniqueItem).type === 'Mission';

const renderTaskLocationLines = (
  taskLocations: string[],
  completedTaskLocations: Set<string>,
  keyPrefix: string
): React.ReactNode[] => {
  const firstIncompleteIndex = taskLocations.findIndex(location => !completedTaskLocations.has(location));

  return taskLocations.map((location, index) => {
    const isCompleted = completedTaskLocations.has(location);
    const isCurrent = !isCompleted && index === firstIncompleteIndex;
    const className = `card-markdown-line task-location-line${isCompleted ? ' task-location-completed' : ''}${isCurrent ? ' task-location-current' : ''}`;

    return (
      <span className={className} key={`${keyPrefix}-task-location-${index}`}>
        {`- ${location}`}
      </span>
    );
  });
};

const renderTaskDescription = (item: CommonItem, keyPrefix: string, digital = false): React.ReactNode => {
  const taskLocations = item.taskLocations ?? [];
  const completedTaskLocations = new Set(item.completedTaskLocations ?? []);
  const reward = item.taskReward?.trim() ?? '';

  return (
    <div className='description-wrapper'>
      {!digital && <span className='card-markdown-line'>
        <strong>Task</strong>
      </span>}
      {renderTaskLocationLines(taskLocations, completedTaskLocations, keyPrefix)}
      {digital ? <SimpleMarkdown text={`**Reward**: ${reward}`} keyPrefix={`${keyPrefix}-reward`} />
        : <span className='card-markdown-line'><strong>Reward</strong>: {reward}</span>}
    </div>
  );
};

const renderMissionDescription = (item: UniqueItem, keyPrefix: string, digital = false): React.ReactNode => {
  const taskLocations = item.taskLocations ?? [];
  const completedTaskLocations = new Set(item.completedTaskLocations ?? []);
  const sacrifice = item.missionSacrifice?.trim() ?? '';
  const reward = item.missionReward?.trim() ?? '';

  return (
    <div className='description-wrapper'>
      {!digital && <span className='card-markdown-line'>
        <strong>Mission</strong>
      </span>}
      {renderTaskLocationLines(taskLocations, completedTaskLocations, keyPrefix)}
      {digital ? <><SimpleMarkdown text={`**Sacrifice**: ${sacrifice}`} keyPrefix={`${keyPrefix}-sacrifice`} />
        <SimpleMarkdown text={`**Reward**: ${reward}`} keyPrefix={`${keyPrefix}-reward`} /></>
        : <><span className='card-markdown-line'><strong>Sacrifice</strong>: {sacrifice}</span>
          <span className='card-markdown-line'><strong>Reward</strong>: {reward}</span></>}
    </div>
  );
};

export const Card: React.FC<CardProps> = ({ card }) => {
  const artwork = useMonsterTokenStyle();
  if (artwork === 'Digital' && card.kind === 'corruption') {
    const corruption = card.item as Corruption;
    const { typeLabel, signColor, dimensionSign, signLabel } = corruptionPresentation(corruption);
    return <DigitalItemCard key={`corruption-${corruption.id}`} card={card}
      rules={<SimpleMarkdown text={corruption.textDescription || ''} keyPrefix={`${corruption.id}-rules`} />}
      typeLabel="" fullArtwork=""
      annotation={<div className="digital-corruption-metadata">
        <strong>{typeLabel}</strong>
        {dimensionSign && <span className={`digital-corruption-sign${signColor ? ` corruption-sign-${signColor.toLowerCase()}` : ''}`}>
          <img src={`/signs/${encodeURIComponent(dimensionSign)}.svg`} alt={signLabel} className="corruption-marker-sign" />
        </span>}
      </div>}
      annotationKey={JSON.stringify([corruption.cardType, corruption.dimensionSign, corruption.signColor])}
      readableContent={<p>{typeLabel}{signLabel ? ` · ${signLabel}` : ''}</p>} />;
  }
  if (artwork === 'Digital' && (card.kind === 'common' || card.kind === 'unique' || card.kind === 'spell' || card.kind === 'ally' || card.kind === 'skill' || card.kind === 'madness' || card.kind === 'injury' || card.kind === 'special')) {
    const description = digitalCardDescription(card.kind, card.item.name, card.item.description || '');
    const rules = isTaskCommonItemCard(card)
      ? renderTaskDescription(card.item as CommonItem, card.item.name, true)
      : isMissionUniqueItemCard(card)
        ? renderMissionDescription(card.item as UniqueItem, card.item.name, true)
        : <SimpleMarkdown text={description?.trim() || ''} keyPrefix={`${card.item.name}-rules`} />;
    const type = (card.item as CommonItem).type;
    const typeLabel = card.kind === 'madness' ? 'Madness' : card.kind === 'injury' ? 'Injury' : card.kind === 'skill' ? 'Skill' : card.kind === 'ally' ? 'Ally' : card.kind === 'spell'
      ? (isMagicalCombatSpell(card.item.name) ? 'Magical spell' : 'Spell')
      : type === 'PhysicalWeapon' ? 'Physical Weapon' : type === 'MagicalWeapon' ? 'Magical Weapon' : type || (card.kind === 'special' ? 'Special' : card.kind === 'common' ? 'Common Item' : (card.item as UniqueItem).isExhibit ? 'Exhibit' : 'Unique Item');
    const fullArtwork = ['skill', 'madness', 'injury'].includes(card.kind) ? '' : getDigitalItemArtworkPath(getItemImagePath(card.kind, card.item.name, (card.item as SpecialCard).isNodensBlessing), false);
    return <DigitalItemCard key={`${card.kind}-${card.item.name}`} card={card} rules={rules} typeLabel={typeLabel} fullArtwork={fullArtwork} />;
  }
  if (card.kind === 'gate') {
    return (
      <div style={{ width: '100%' }}>
        <GateMarker gate={card.item as any as Gate} />
      </div>
    )
  }
  if (card.kind === 'investigator') {
    return (
      <div style={{
        width: '100%'
      }}>
        <InvestigatorDisplay investigator={card.item as Investigator} />
      </div>
    )
  }
  if (card.kind === 'personalStory') {
    return <PersonalStoryCard card={card.item} />
  }
  if (card.kind === 'relationship') {
    return <RelationshipCard card={card.item as Relationship} />;
  }
  if (card.kind === 'corruption') {
    const corruption = card.item as Corruption;
    const { cardType, signColor, dimensionSign } = corruptionPresentation(corruption);
    return (
      <div className={`card corruption-card corruption-${cardType?.toLowerCase() ?? 'unknown'}`}>
        <div className="corruption-card-type">Corruption</div>
        <div className="corruption-card-name">{corruption.name}</div>
        {!!corruption.textDescription && (
          <div className="corruption-card-description">
            <SimpleMarkdown
              text={corruption.textDescription}
              keyPrefix={`${corruption.id}-description`}
            />
          </div>
        )}
        {dimensionSign && <div className={`corruption-card-sign${signColor ? ` corruption-sign-${signColor.toLowerCase()}` : ''}`}>
          <img
            loading="lazy"
            src={`/signs/${encodeURIComponent(dimensionSign)}.svg`}
            alt={`${dimensionSign} dimension sign`}
            className="corruption-marker-sign"
          />
        </div>}
      </div>
    );
  }

  const imgPath = getItemImagePath(card.kind, (card.item.name || card.item) as any, (card.item as SpecialCard).isNodensBlessing || false);
  const title = card.item.title?.trim();
  const description = getDescription(card);
  const isTaskCard = isTaskCommonItemCard(card);
  const isMissionCard = isMissionUniqueItemCard(card);
  const displayTitle = title || card.item.name;
  const cover = getTitleCover(card.kind);
  const isTome =
    (card.kind === 'common' || card.kind === 'unique') &&
    (card.item as CommonItem | UniqueItem).type === 'Tome';

  return (
    <div className='card'>
      <img loading="lazy"
        src={imgPath}
        alt={displayTitle}
        onError={(event) => {
          event.currentTarget.style.display = 'none';
        }}
      />
      {card.item.usesRemaining !== undefined && card.item.usesRemaining !== null && (
        <div className="uses-badge" aria-label={`${card.kind === 'unique' && card.item.name === 'Ritual Candles' ? 'Clues on card' : 'Uses remaining'}: ${card.item.usesRemaining}`}>{card.item.usesRemaining}</div>
      )}
      {!!cover && <div className={`card-title-cover ${card.kind.toLocaleLowerCase()}`}>
        <img loading="lazy" src={cover} alt="title cover"></img></div>}
      {!!title && <div className={`card-title ${card.kind.toLocaleLowerCase()} ${card.item.name}`}>{title}</div>}
      {(isTaskCard || isMissionCard || !!description) && (
        <div className={`card-description ${card.kind.toLocaleLowerCase()} ${card.item.name} ${isMissionCard ? 'mission' : ''} ${isTome ? 'tome' : ''}`}>
          {isTaskCard
            ? renderTaskDescription(card.item as CommonItem, `${card.item.name}-description`)
            : isMissionCard
              ? renderMissionDescription(card.item as UniqueItem, `${card.item.name}-description`)
              : <SimpleMarkdown text={description} keyPrefix={`${card.item.name}-description`} />}
        </div>
      )}
    </div>
  )
}
